// src/components/admin/nornsweave/modals/YearSetupWizard.js
import React, { useState } from 'react';
import TimePicker, { formatTime12 } from '../../../TimePicker';

const STEP_TITLES = [
  'Year Dates',
  'Religious Calendar',
  'Extended Holidays',
  'Closures & Half Days',
  'Library Events',
  'Inventory',
  'Review'
];

function YearSetupWizard({
  yearSetupForm,
  setYearSetupForm,
  yearSetupStep,
  setYearSetupStep,
  wizardHolyDays,
  setWizardHolyDays,
  wizardAdorationDays,
  setWizardAdorationDays,
  wizardHolidays,
  setWizardHolidays,
  wizardHalfDays,
  setWizardHalfDays,
  wizardWorkdays,
  setWizardWorkdays,
  wizardLibraryEvents,
  setWizardLibraryEvents,
  wizardSaturdayConfig,
  setWizardSaturdayConfig,
  getDayOfWeekFromDate,
  showNotification,
  onClose,
  onSave,
  // Copy from previous year props
  hasPreviousYearData,
  previousYearLabel,
  onCopyFromPreviousYear,
  isCopyingYear
}) {
  // Extended holidays state (multi-day breaks)
  const [extendedHolidays, setExtendedHolidays] = useState([
    { id: 'thanksgiving', name: 'Thanksgiving Break', startDate: '', endDate: '', type: 'extended' },
    { id: 'christmas', name: 'Christmas Break', startDate: '', endDate: '', type: 'extended' },
    { id: 'spring', name: 'Spring Break', startDate: '', endDate: '', type: 'extended' }
  ]);

  // Inventory period state
  const [inventoryPeriod, setInventoryPeriod] = useState({
    startDate: '',
    endDate: '',
    enabled: false
  });

  // Collapsed sections for step 2
  const [expandedSections, setExpandedSections] = useState({
    mass: true,
    holyDays: false,
    adoration: false
  });

  // Saturday date picker state
  const [saturdayDateToAdd, setSaturdayDateToAdd] = useState('');

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const totalSteps = 7;

  // Navigation helpers
  const canGoNext = () => {
    switch (yearSetupStep) {
      case 1:
        return yearSetupForm.academicYearStart && yearSetupForm.academicYearEnd;
      default:
        return true;
    }
  };

  const goNext = () => {
    if (yearSetupStep < totalSteps) {
      setYearSetupStep(yearSetupStep + 1);
    }
  };

  const goBack = () => {
    if (yearSetupStep > 1) {
      setYearSetupStep(yearSetupStep - 1);
    }
  };

  const handleSave = () => {
    // Merge extended holidays into wizardHolidays
    const extendedToAdd = extendedHolidays.filter(h => h.startDate).map(h => ({
      name: h.name,
      startDate: h.startDate,
      endDate: h.endDate || h.startDate,
      type: 'extended'
    }));

    // Filter out any existing extended types to avoid duplicates, then add new ones
    const nonExtendedHolidays = wizardHolidays.filter(h => h.type !== 'extended');
    setWizardHolidays([...nonExtendedHolidays, ...extendedToAdd]);

    // Add inventory to library events if enabled
    if (inventoryPeriod.enabled && inventoryPeriod.startDate) {
      const existingNonInventory = (wizardLibraryEvents || []).filter(e => e.eventType !== 'inventory');
      const inventoryEvent = {
        eventType: 'inventory',
        name: 'Library Inventory',
        startDate: inventoryPeriod.startDate,
        endDate: inventoryPeriod.endDate || inventoryPeriod.startDate,
        blocksAllBookings: true
      };
      setWizardLibraryEvents([...existingNonInventory, inventoryEvent]);
    }

    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-blue-900">
            Academic Year Setup
          </h3>
          <div className="flex items-center gap-1">
            {STEP_TITLES.map((title, idx) => (
              <div key={idx} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    yearSetupStep === idx + 1
                      ? 'bg-blue-600 text-white'
                      : yearSetupStep > idx + 1
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                  title={title}
                >
                  {yearSetupStep > idx + 1 ? '✓' : idx + 1}
                </div>
                {idx < STEP_TITLES.length - 1 && (
                  <div className={`w-4 h-0.5 ${yearSetupStep > idx + 1 ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4">Step {yearSetupStep} of {totalSteps}: {STEP_TITLES[yearSetupStep - 1]}</p>

        {/* Step 1: Year Dates */}
        {yearSetupStep === 1 && (
          <div className="space-y-6">
            {/* Copy from Previous Year Option */}
            {hasPreviousYearData && (
              <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-indigo-800 flex items-center gap-2">
                      <span className="text-lg">📋</span> Copy from Previous Year ({previousYearLabel})
                    </h4>
                    <p className="text-indigo-600 text-sm mt-1">
                      Import calendar events with dates automatically shifted to match the same day-of-week in the new year.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onCopyFromPreviousYear(yearSetupForm.academicYearStart)}
                    disabled={!yearSetupForm.academicYearStart || isCopyingYear}
                    className={`ml-4 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      !yearSetupForm.academicYearStart || isCopyingYear
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {isCopyingYear ? 'Copying...' : 'Copy & Shift Dates'}
                  </button>
                </div>
                {!yearSetupForm.academicYearStart && (
                  <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                    <span>⚠️</span> Enter the new Year Start Date below first to enable copying
                  </p>
                )}
              </div>
            )}

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-3">Academic Year (Calendar Boundaries)</h4>
              <p className="text-blue-600 text-sm mb-3">The full date range for your calendar (includes teacher workdays before/after school)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-blue-900 text-sm font-semibold mb-2">Year Start Date</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    value={yearSetupForm.academicYearStart}
                    onChange={(e) => setYearSetupForm({...yearSetupForm, academicYearStart: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-blue-900 text-sm font-semibold mb-2">Year End Date</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                    value={yearSetupForm.academicYearEnd}
                    onChange={(e) => setYearSetupForm({...yearSetupForm, academicYearEnd: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">School Days (Students Present)</h4>
              <p className="text-green-600 text-sm mb-3">Days outside this range will show as "Teacher Workday" (no fixed classes)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-green-900 text-sm font-semibold mb-2">First Day of School</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                    value={yearSetupForm.schoolStartDate}
                    onChange={(e) => setYearSetupForm({...yearSetupForm, schoolStartDate: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-green-900 text-sm font-semibold mb-2">Last Day of School</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                    value={yearSetupForm.schoolEndDate}
                    onChange={(e) => setYearSetupForm({...yearSetupForm, schoolEndDate: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Saturday Hours Configuration */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-amber-800">Saturday Library Hours</h4>
                  <p className="text-amber-600 text-sm">Open the library one Saturday per month</p>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <span className="text-amber-700 font-medium text-sm">Enable</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={wizardSaturdayConfig?.enabled || false}
                      onChange={(e) => setWizardSaturdayConfig({
                        ...wizardSaturdayConfig,
                        enabled: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </div>
                </label>
              </div>

              {wizardSaturdayConfig?.enabled && (
                <div className="space-y-4 pt-3 border-t border-amber-200">
                  {/* Saturday Hours */}
                  <div>
                    <p className="text-amber-700 text-sm mb-2 font-medium">Default Saturday Hours (applies to both libraries)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <TimePicker
                        label="Open"
                        value={wizardSaturdayConfig?.startTime || '09:00'}
                        onChange={(val) => setWizardSaturdayConfig({
                          ...wizardSaturdayConfig,
                          startTime: val
                        })}
                      />
                      <TimePicker
                        label="Close"
                        value={wizardSaturdayConfig?.endTime || '13:00'}
                        onChange={(val) => setWizardSaturdayConfig({
                          ...wizardSaturdayConfig,
                          endTime: val
                        })}
                      />
                    </div>
                  </div>

                  {/* Add Saturday Dates */}
                  <div>
                    <p className="text-amber-700 text-sm mb-2 font-medium">Select Saturday Dates</p>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        className="flex-1 px-3 py-2 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none text-sm"
                        value={saturdayDateToAdd}
                        onChange={(e) => setSaturdayDateToAdd(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!saturdayDateToAdd) {
                            showNotification('Please select a date', 'error');
                            return;
                          }
                          const dayOfWeek = getDayOfWeekFromDate(saturdayDateToAdd);
                          if (dayOfWeek !== 'Saturday') {
                            showNotification('Please select a Saturday', 'error');
                            return;
                          }
                          // Support both old format (string) and new format (object)
                          const existingDates = (wizardSaturdayConfig?.dates || []).map(d =>
                            typeof d === 'string' ? d : d.date
                          );
                          if (existingDates.includes(saturdayDateToAdd)) {
                            showNotification('This date is already added', 'error');
                            return;
                          }
                          // Add as object with date and empty description
                          const newDateObj = { date: saturdayDateToAdd, description: '' };
                          const newDates = [...(wizardSaturdayConfig?.dates || []), newDateObj]
                            .sort((a, b) => {
                              const dateA = typeof a === 'string' ? a : a.date;
                              const dateB = typeof b === 'string' ? b : b.date;
                              return dateA.localeCompare(dateB);
                            });
                          setWizardSaturdayConfig({
                            ...wizardSaturdayConfig,
                            dates: newDates
                          });
                          setSaturdayDateToAdd('');
                        }}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* List of Saturday Dates with Descriptions */}
                  {(wizardSaturdayConfig?.dates || []).length > 0 && (
                    <div>
                      <p className="text-amber-700 text-sm mb-2 font-medium">
                        Open Saturdays ({(wizardSaturdayConfig?.dates || []).length})
                      </p>
                      <div className="space-y-2">
                        {(wizardSaturdayConfig?.dates || []).map((dateItem, idx) => {
                          // Support both old format (string) and new format (object)
                          const dateStr = typeof dateItem === 'string' ? dateItem : dateItem.date;
                          const description = typeof dateItem === 'string' ? '' : (dateItem.description || '');
                          return (
                            <div
                              key={dateStr}
                              className="bg-white p-3 rounded-lg border border-amber-300"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-amber-800 font-medium">
                                  {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setWizardSaturdayConfig({
                                      ...wizardSaturdayConfig,
                                      dates: (wizardSaturdayConfig?.dates || []).filter((d, i) => i !== idx)
                                    });
                                  }}
                                  className="text-red-500 hover:text-red-700 font-bold text-lg"
                                >
                                  ×
                                </button>
                              </div>
                              <input
                                type="text"
                                placeholder="Description (e.g., 'Monthly Library Saturday', 'Book Fair')"
                                className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none text-sm"
                                value={description}
                                onChange={(e) => {
                                  const newDates = [...(wizardSaturdayConfig?.dates || [])];
                                  newDates[idx] = { date: dateStr, description: e.target.value };
                                  setWizardSaturdayConfig({
                                    ...wizardSaturdayConfig,
                                    dates: newDates
                                  });
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(wizardSaturdayConfig?.dates || []).length === 0 && (
                    <p className="text-amber-600 text-sm italic">No Saturday dates added yet. Select dates above.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Religious Calendar (Combined) */}
        {yearSetupStep === 2 && (
          <div className="space-y-4">
            {/* Mass Schedule Section */}
            <div className="border-2 border-violet-200 rounded-xl overflow-hidden">
              <div
                className="bg-violet-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-violet-100"
                onClick={() => toggleSection('mass')}
              >
                <h4 className="font-bold text-violet-800 flex items-center gap-2">
                  <span className={`transition-transform ${expandedSections.mass ? 'rotate-90' : ''}`}>▶</span>
                  Regular Mass Schedule
                </h4>
              </div>
              {expandedSections.mass && (
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-violet-900 text-sm font-semibold mb-2">Regular Mass Day</label>
                    <select
                      className="w-full px-4 py-3 border-2 border-violet-200 rounded-lg"
                      value={yearSetupForm.massDay}
                      onChange={(e) => setYearSetupForm({...yearSetupForm, massDay: e.target.value})}
                    >
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <TimePicker label="Mass Start Time" value={yearSetupForm.massStartTime} onChange={(val) => setYearSetupForm({...yearSetupForm, massStartTime: val})} required />
                    <TimePicker label="Mass End Time" value={yearSetupForm.massEndTime} onChange={(val) => setYearSetupForm({...yearSetupForm, massEndTime: val})} required />
                  </div>
                </div>
              )}
            </div>

            {/* Holy Days Section */}
            <div className="border-2 border-purple-200 rounded-xl overflow-hidden">
              <div
                className="bg-purple-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-purple-100"
                onClick={() => toggleSection('holyDays')}
              >
                <h4 className="font-bold text-purple-800 flex items-center gap-2">
                  <span className={`transition-transform ${expandedSections.holyDays ? 'rotate-90' : ''}`}>▶</span>
                  Holy Days ({wizardHolyDays.filter(h => h.name && h.date).length})
                </h4>
              </div>
              {expandedSections.holyDays && (
                <div className="p-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700 mb-4">
                    <p>On Holy Days, Mass moves FROM {yearSetupForm.massDay || 'regular day'} TO the Holy Day.</p>
                  </div>
                  <div className="space-y-2">
                    {wizardHolyDays.map((hd, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-purple-200">
                        <input type="text" placeholder="Holy Day Name" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={hd.name} onChange={(e) => { const updated = [...wizardHolyDays]; updated[idx].name = e.target.value; setWizardHolyDays(updated); }} />
                        <input type="date" className="px-3 py-2 border border-slate-300 rounded-lg text-sm" value={hd.date} onChange={(e) => { const updated = [...wizardHolyDays]; updated[idx].date = e.target.value; setWizardHolyDays(updated); }} />
                        <span className="text-sm text-slate-500 min-w-[60px]">{hd.date ? getDayOfWeekFromDate(hd.date).slice(0, 3) : ''}</span>
                        <button onClick={() => setWizardHolyDays(wizardHolyDays.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">✕</button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setWizardHolyDays([...wizardHolyDays, { name: '', date: '' }])} className="w-full py-2 mt-3 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:bg-purple-50 text-sm">+ Add Holy Day</button>
                </div>
              )}
            </div>

            {/* Adoration Days Section */}
            <div className="border-2 border-indigo-200 rounded-xl overflow-hidden">
              <div
                className="bg-indigo-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-indigo-100"
                onClick={() => toggleSection('adoration')}
              >
                <h4 className="font-bold text-indigo-800 flex items-center gap-2">
                  <span className={`transition-transform ${expandedSections.adoration ? 'rotate-90' : ''}`}>▶</span>
                  Adoration Days ({wizardAdorationDays.filter(a => a.date).length})
                </h4>
              </div>
              {expandedSections.adoration && (
                <div className="p-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700 mb-4">
                    <p>Adoration is on Fridays at the same time as Mass ({formatTime12(yearSetupForm.massStartTime)} - {formatTime12(yearSetupForm.massEndTime)}).</p>
                  </div>
                  <div className="space-y-2">
                    {wizardAdorationDays.map((ad, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-indigo-200">
                        <input type="date" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={ad.date} onChange={(e) => { const dayOfWeek = getDayOfWeekFromDate(e.target.value); if (dayOfWeek !== 'Friday') { showNotification('Adoration days must be on Fridays', 'error'); return; } const updated = [...wizardAdorationDays]; updated[idx].date = e.target.value; setWizardAdorationDays(updated); }} />
                        <span className="text-sm text-slate-500">{ad.date ? new Date(ad.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Select a Friday'}</span>
                        <button onClick={() => setWizardAdorationDays(wizardAdorationDays.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">✕</button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setWizardAdorationDays([...wizardAdorationDays, { date: '' }])} className="w-full py-2 mt-3 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 text-sm">+ Add Adoration Date</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Extended Holidays (Multi-day breaks) */}
        {yearSetupStep === 3 && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
              <p><strong>Extended Holidays</strong> are multi-day breaks like Thanksgiving, Christmas, and Spring Break.</p>
              <p className="mt-1">These will be treated differently for email notifications.</p>
            </div>

            {extendedHolidays.filter(h => !h.isCustom).map((holiday, idx) => {
              const realIdx = extendedHolidays.findIndex(h => h.id === holiday.id);
              return (
                <div key={holiday.id} className="bg-white rounded-lg p-4 border-2 border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">{holiday.name}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-700 text-sm mb-1">Start Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={holiday.startDate}
                        onChange={(e) => {
                          const updated = [...extendedHolidays];
                          updated[realIdx].startDate = e.target.value;
                          setExtendedHolidays(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 text-sm mb-1">End Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={holiday.endDate}
                        onChange={(e) => {
                          const updated = [...extendedHolidays];
                          updated[realIdx].endDate = e.target.value;
                          setExtendedHolidays(updated);
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Custom breaks */}
            {extendedHolidays.filter(h => h.isCustom).map((holiday) => {
              const realIdx = extendedHolidays.findIndex(h => h.id === holiday.id);
              return (
                <div key={holiday.id} className="bg-white rounded-lg p-4 border-2 border-red-200">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="text"
                      placeholder="Break Name"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg font-semibold"
                      value={holiday.name}
                      onChange={(e) => {
                        const updated = [...extendedHolidays];
                        updated[realIdx].name = e.target.value;
                        setExtendedHolidays(updated);
                      }}
                    />
                    <button
                      onClick={() => setExtendedHolidays(extendedHolidays.filter(h => h.id !== holiday.id))}
                      className="text-red-500 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-700 text-sm mb-1">Start Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={holiday.startDate}
                        onChange={(e) => {
                          const updated = [...extendedHolidays];
                          updated[realIdx].startDate = e.target.value;
                          setExtendedHolidays(updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 text-sm mb-1">End Date</label>
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={holiday.endDate}
                        onChange={(e) => {
                          const updated = [...extendedHolidays];
                          updated[realIdx].endDate = e.target.value;
                          setExtendedHolidays(updated);
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setExtendedHolidays([...extendedHolidays, { id: Date.now().toString(), name: '', startDate: '', endDate: '', type: 'extended', isCustom: true }])}
              className="w-full py-3 border-2 border-dashed border-red-300 rounded-lg text-red-600 hover:bg-red-50 font-medium"
            >
              + Add Another Break
            </button>
          </div>
        )}

        {/* Step 4: Single-Day Closures & Half Days */}
        {yearSetupStep === 4 && (
          <div className="space-y-6">
            {/* Single-Day Holidays */}
            <div>
              <h4 className="font-bold text-red-800 mb-3">Single-Day Holidays (School Closed)</h4>
              <div className="space-y-2">
                {wizardHolidays.filter(h => !h.type || h.type !== 'extended').map((h, idx) => {
                  const realIdx = wizardHolidays.findIndex(wh => wh === h);
                  return (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-red-200">
                      <input type="text" placeholder="Holiday Name" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={h.name} onChange={(e) => { const updated = [...wizardHolidays]; updated[realIdx].name = e.target.value; setWizardHolidays(updated); }} />
                      <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={h.startDate} onChange={(e) => { const updated = [...wizardHolidays]; updated[realIdx].startDate = e.target.value; updated[realIdx].endDate = e.target.value; setWizardHolidays(updated); }} />
                      <button onClick={() => setWizardHolidays(wizardHolidays.filter((_, i) => i !== realIdx))} className="text-red-500 hover:text-red-700">✕</button>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => setWizardHolidays([...wizardHolidays, { name: '', startDate: '', endDate: '' }])} className="w-full py-2 mt-2 border-2 border-dashed border-red-200 rounded-lg text-red-600 hover:bg-red-50 text-sm font-medium">+ Add Holiday</button>
            </div>

            {/* Half Days */}
            <div>
              <h4 className="font-bold text-orange-800 mb-3">Half Days</h4>
              <div className="space-y-2">
                {wizardHalfDays.map((h, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-orange-200">
                    <input type="text" placeholder="Description" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={h.name} onChange={(e) => { const updated = [...wizardHalfDays]; updated[idx].name = e.target.value; setWizardHalfDays(updated); }} />
                    <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={h.date} onChange={(e) => { const updated = [...wizardHalfDays]; updated[idx].date = e.target.value; setWizardHalfDays(updated); }} />
                    <select className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={h.period} onChange={(e) => { const updated = [...wizardHalfDays]; updated[idx].period = e.target.value; setWizardHalfDays(updated); }}>
                      <option value="afternoon">PM off</option>
                      <option value="morning">AM only</option>
                    </select>
                    <button onClick={() => setWizardHalfDays(wizardHalfDays.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">✕</button>
                  </div>
                ))}
              </div>
              <button onClick={() => setWizardHalfDays([...wizardHalfDays, { name: '', date: '', period: 'afternoon' }])} className="w-full py-2 mt-2 border-2 border-dashed border-orange-200 rounded-lg text-orange-600 hover:bg-orange-50 text-sm font-medium">+ Add Half Day</button>
            </div>

            {/* Teacher Workdays */}
            <div>
              <h4 className="font-bold text-slate-800 mb-3">Teacher Workdays (School Closed, You Working)</h4>
              <div className="space-y-2">
                {wizardWorkdays.map((w, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-slate-200">
                    <input type="text" placeholder="Description" className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" value={w.name} onChange={(e) => { const updated = [...wizardWorkdays]; updated[idx].name = e.target.value; setWizardWorkdays(updated); }} />
                    <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={w.startDate} onChange={(e) => { const updated = [...wizardWorkdays]; updated[idx].startDate = e.target.value; setWizardWorkdays(updated); }} />
                    <span className="text-slate-400 text-sm">to</span>
                    <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={w.endDate} onChange={(e) => { const updated = [...wizardWorkdays]; updated[idx].endDate = e.target.value; setWizardWorkdays(updated); }} />
                    <button onClick={() => setWizardWorkdays(wizardWorkdays.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">✕</button>
                  </div>
                ))}
              </div>
              <button onClick={() => setWizardWorkdays([...wizardWorkdays, { name: '', startDate: '', endDate: '', libraryOpen: false }])} className="w-full py-2 mt-2 border-2 border-dashed border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium">+ Add Workday</button>
            </div>
          </div>
        )}

        {/* Step 5: Library Events */}
        {yearSetupStep === 5 && (
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-700 mb-4">
              <p><strong>Library Events</strong> block the library for bookings during their date range.</p>
              <p className="mt-1">Faculty Meetings will block all bookings. Other events block class bookings but allow admin bookings.</p>
            </div>

            <div className="space-y-3">
              {(wizardLibraryEvents || []).filter(e => e.eventType !== 'inventory').map((e, idx) => {
                const realIdx = (wizardLibraryEvents || []).findIndex(we => we === e);
                return (
                  <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-teal-200 flex-wrap">
                    <select
                      className="px-2 py-2 border border-slate-300 rounded-lg text-sm"
                      value={e.eventType}
                      onChange={(ev) => { const updated = [...wizardLibraryEvents]; updated[realIdx].eventType = ev.target.value; setWizardLibraryEvents(updated); }}
                    >
                      <option value="book-fair">Book Fair</option>
                      <option value="tla">TLA/PD</option>
                      <option value="diocesan-meeting">Librarian Meeting</option>
                      <option value="faculty-meeting">Faculty Meeting</option>
                      <option value="other">Other</option>
                    </select>
                    <input type="text" placeholder="Name/Description" className="flex-1 min-w-[120px] px-3 py-2 border border-slate-300 rounded-lg text-sm" value={e.name} onChange={(ev) => { const updated = [...wizardLibraryEvents]; updated[realIdx].name = ev.target.value; setWizardLibraryEvents(updated); }} />
                    <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={e.startDate} onChange={(ev) => { const updated = [...wizardLibraryEvents]; updated[realIdx].startDate = ev.target.value; setWizardLibraryEvents(updated); }} />
                    <span className="text-slate-400 text-sm">to</span>
                    <input type="date" className="px-2 py-2 border border-slate-300 rounded-lg text-sm" value={e.endDate} onChange={(ev) => { const updated = [...wizardLibraryEvents]; updated[realIdx].endDate = ev.target.value; setWizardLibraryEvents(updated); }} />
                    <button onClick={() => setWizardLibraryEvents(wizardLibraryEvents.filter((_, i) => i !== realIdx))} className="text-red-500 hover:text-red-700">✕</button>
                  </div>
                );
              })}
            </div>

            <button onClick={() => setWizardLibraryEvents([...(wizardLibraryEvents || []), { eventType: 'book-fair', name: '', startDate: '', endDate: '' }])} className="w-full py-3 border-2 border-dashed border-teal-300 rounded-lg text-teal-600 hover:bg-teal-50 font-medium">+ Add Library Event</button>
          </div>
        )}

        {/* Step 6: Inventory */}
        {yearSetupStep === 6 && (
          <div className="space-y-6">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-amber-800 text-lg">End of Year Inventory</h4>
                <label className="flex items-center gap-3 cursor-pointer">
                  <span className="text-amber-700 font-medium">Enable</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={inventoryPeriod.enabled}
                      onChange={(e) => setInventoryPeriod({...inventoryPeriod, enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-amber-500"></div>
                  </div>
                </label>
              </div>

              {inventoryPeriod.enabled && (
                <>
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4 text-sm text-amber-800">
                    <p><strong>During inventory:</strong></p>
                    <ul className="list-disc ml-4 mt-1">
                      <li>All classes will be cancelled</li>
                      <li>Only admin bookings are allowed</li>
                      <li>Teachers will receive an email <strong>2 weeks before</strong> reminding students to return books</li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-amber-900 text-sm font-semibold mb-2">Inventory Start Date</label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 border-2 border-amber-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                        value={inventoryPeriod.startDate}
                        onChange={(e) => setInventoryPeriod({...inventoryPeriod, startDate: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-amber-900 text-sm font-semibold mb-2">Inventory End Date</label>
                      <input
                        type="date"
                        className="w-full px-4 py-3 border-2 border-amber-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
                        value={inventoryPeriod.endDate}
                        onChange={(e) => setInventoryPeriod({...inventoryPeriod, endDate: e.target.value})}
                      />
                    </div>
                  </div>
                </>
              )}

              {!inventoryPeriod.enabled && (
                <p className="text-amber-600 text-sm">Enable to set up end-of-year inventory period.</p>
              )}
            </div>
          </div>
        )}

        {/* Step 7: Review */}
        {yearSetupStep === 7 && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-bold text-green-800 mb-3">Review Your Setup</h4>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Academic Year:</span>
                  <span className="font-medium">{yearSetupForm.academicYearStart} to {yearSetupForm.academicYearEnd}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">School Days:</span>
                  <span className="font-medium">{yearSetupForm.schoolStartDate || 'Not set'} to {yearSetupForm.schoolEndDate || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Regular Mass:</span>
                  <span className="font-medium">{yearSetupForm.massDay}s, {formatTime12(yearSetupForm.massStartTime)} - {formatTime12(yearSetupForm.massEndTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Holy Days:</span>
                  <span className="font-medium">{wizardHolyDays.filter(h => h.name && h.date).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Adoration Days:</span>
                  <span className="font-medium">{wizardAdorationDays.filter(a => a.date).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Extended Holidays:</span>
                  <span className="font-medium">{extendedHolidays.filter(h => h.startDate).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Single-Day Holidays:</span>
                  <span className="font-medium">{wizardHolidays.filter(h => h.name && !h.type).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Half Days:</span>
                  <span className="font-medium">{wizardHalfDays.filter(h => h.date).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Library Events:</span>
                  <span className="font-medium">{(wizardLibraryEvents || []).filter(e => e.name && e.eventType !== 'inventory').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Inventory Period:</span>
                  <span className="font-medium">{inventoryPeriod.enabled ? `${inventoryPeriod.startDate} to ${inventoryPeriod.endDate}` : 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Saturday Hours:</span>
                  <span className="font-medium">
                    {wizardSaturdayConfig?.enabled
                      ? `${formatTime12(wizardSaturdayConfig.startTime || '09:00')} - ${formatTime12(wizardSaturdayConfig.endTime || '13:00')} (${(wizardSaturdayConfig.dates || []).length} dates)`
                      : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
              <p><strong>Note:</strong> All events can be edited after saving from the Year Setup tab.</p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200">
          {yearSetupStep > 1 ? (
            <button type="button" onClick={goBack} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold">
              ← Back
            </button>
          ) : (
            <button type="button" onClick={onClose} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold">
              Cancel
            </button>
          )}

          {yearSetupStep < totalSteps ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext()}
              className={`flex-1 py-3 rounded-lg transition-colors font-semibold ${
                canGoNext()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue →
            </button>
          ) : (
            <button type="button" onClick={handleSave} className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold">
              ✓ Complete Setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default YearSetupWizard;
